// components/home/ServicesOverview.jsx
import React from 'react';
import { motion } from 'framer-motion';
import { 
  FaUserMd, 
  FaMicroscope, 
  FaXRay, 
  FaCapsules, 
  FaHeartbeat,
  FaBaby,
  FaProcedures,
  FaFileMedical,
  FaUsers,
  FaStethoscope,
  FaFlask,
  FaClinicMedical
} from 'react-icons/fa';
import { GiHealing } from 'react-icons/gi';
import { MdPregnantWoman, MdFamilyRestroom } from 'react-icons/md';
import { TbCertificate } from 'react-icons/tb';

const ServicesOverview = () => {
    const services = [
        {
            icon: <FaProcedures className="text-2xl text-blue-600" />,
            title: "Surgical Services",
            description: "Medium surgical procedures with modern operation theaters and post-operative care",
            features: ["Minor Procedures", "Post-op Recovery"],
            color: "bg-blue-50 border-blue-200"
        },
        {
            icon: <TbCertificate className="text-2xl text-green-600" />,
            title: "Medical Certification",
            description: "Official medical certificates for employment, education, and legal purposes",
            features: ["Health Clearance", "Fitness Certificates", "Legal Documentation"],
            color: "bg-green-50 border-green-200"
        },
        {
            icon: <MdFamilyRestroom className="text-2xl text-purple-600" />,
            title: "Family Planning",
            description: "Comprehensive family planning services and reproductive health counseling",
            features: ["Contraceptive Counseling", "FP Methods", "Reproductive Health"],
            color: "bg-purple-50 border-purple-200"
        },
        {
            icon: <MdPregnantWoman className="text-2xl text-pink-600" />,
            title: "Maternal Care",
            description: "Complete antenatal and postnatal care for mothers and newborns",
            features: ["Antenatal Checkups", "Delivery Services", "Postnatal Care"],
            color: "bg-pink-50 border-pink-200"
        },
        {
            icon: <FaUserMd className="text-2xl text-indigo-600" />,
            title: "Outpatient Clinic",
            description: "General consultation and specialized outpatient services",
            features: ["Doctor Consultation", "Chronic Disease Mgmt", "Health Screening"],
            color: "bg-indigo-50 border-indigo-200"
        },
        {
            icon: <FaMicroscope className="text-2xl text-amber-600" />,
            title: "Laboratory Services",
            description: "Comprehensive diagnostic testing with rapid results",
            features: ["Blood Tests", "Microbiology"],
            color: "bg-amber-50 border-amber-200"
        },
        {
            icon: <FaXRay className="text-2xl text-cyan-600" />,
            title: "Imaging & Radiology",
            description: "Advanced imaging services including Ultrasound",
            features: ["Ultrasound", "ECG Services"],
            color: "bg-cyan-50 border-cyan-200"
        },
        {
            icon: <FaCapsules className="text-2xl text-teal-600" />,
            title: "Pharmacy Services",
            description: "24/7 pharmacy with quality medications and prescription services",
            features: ["Essential Medicines", "Free Dispensing", "Medication Counseling"],
            color: "bg-teal-50 border-teal-200"
        },
        {
            icon: <FaHeartbeat className="text-2xl text-red-600" />,
            title: "Emergency Care",
            description: "24/7 emergency medical services with rapid response",
            features: ["Trauma Care", "Emergency Resuscitation"],
            color: "bg-red-50 border-red-200"
        },
        {
            icon: <FaBaby className="text-2xl text-emerald-600" />,
            title: "Child Health",
            description: "Pediatric care, immunization, and child growth monitoring",
            features: ["Vaccination", "Growth Monitoring", "Pediatric Consultation"],
            color: "bg-emerald-50 border-emerald-200"
        },
        {
            icon: <FaFlask className="text-2xl text-orange-600" />,
            title: "Dental Services",
            description: "Comprehensive dental care and oral health services",
            features: ["Dental Checkup", "Tooth Extraction", "Oral Hygiene"],
            color: "bg-orange-50 border-orange-200"
        },
        {
            icon: <GiHealing className="text-2xl text-violet-600" />,
            title: "Chronic Disease Management",
            description: "Specialized care for diabetes, hypertension, and other chronic conditions",
            features: ["Diabetes Care", "Hypertension Mgmt", "Follow-up Services"],
            color: "bg-violet-50 border-violet-200"
        }
    ];

    return (
        <section className="py-20 bg-gradient-to-b from-gray-50 to-white" id="services">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header Section */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    viewport={{ once: true, margin: "-100px" }}
                    className="text-center mb-16"
                >
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-6">
                        <FaClinicMedical className="text-3xl text-blue-600" />
                    </div>
                    <h2 className="text-xl md:text-xl font-bold text-gray-900 mb-4">
                        Comprehensive <span className="text-blue-600">Medical Services</span>
                    </h2>
                    <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
                        Providing holistic healthcare with compassion, expertise, and state-of-the-art facilities
                    </p>
                    <div className="w-24 h-1 bg-blue-600 mx-auto rounded-full"></div>
                </motion.div>

                {/* Services Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {services.map((service, index) => (
                        <motion.div
                            key={index}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: index * 0.05 }}
                            viewport={{ once: true, margin: "-50px" }}
                            whileHover={{ 
                                y: -5,
                                boxShadow: "0 20px 40px rgba(0, 0, 0, 0.1)"
                            }}
                            className={`relative group rounded-2xl border-2 ${service.color} p-6 transition-all duration-300 hover:border-blue-300 overflow-hidden`}
                        >
                            {/* Background Pattern */}
                            <div className="absolute inset-0 opacity-5 group-hover:opacity-10 transition-opacity duration-300">
                                <div className="absolute -top-4 -right-4 w-20 h-20 rounded-full bg-blue-200"></div>
                                <div className="absolute -bottom-4 -left-4 w-16 h-16 rounded-full bg-green-200"></div>
                            </div>

                            {/* Icon */}
                            <div className="relative z-10">
                                <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-white shadow-md mb-5 group-hover:scale-110 transition-transform duration-300">
                                    {service.icon}
                                </div>
                                
                                {/* Title & Description */}
                                <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-700 transition-colors">
                                    {service.title}
                                </h3>
                                <p className="text-gray-700 mb-4 text-sm leading-relaxed">
                                    {service.description}
                                </p>

                                {/* Features */}
                                <ul className="space-y-2 mb-6">
                                    {service.features.map((feature, idx) => (
                                        <li key={idx} className="flex items-center text-sm text-gray-600">
                                            <svg className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                            </svg>
                                            <span>{feature}</span>
                                        </li>
                                    ))}
                                </ul>

                                {/* Learn More Link */}
                                <div className="pt-4 border-t border-gray-200">
                                    <button className="text-blue-600 hover:text-blue-800 font-medium text-sm flex items-center group-hover:translate-x-2 transition-transform duration-300">
                                        Learn more
                                        <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>

                {/* Call to Action */}
                <motion.div
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    transition={{ delay: 0.5 }}
                    viewport={{ once: true }}
                    className="mt-16 text-center"
                >
                    <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-2xl p-8 md:p-12 border border-blue-100">
                        <div className="max-w-3xl mx-auto">
                            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                                Need a specific service or consultation?
                            </h3>
                            <p className="text-gray-700 mb-8 text-lg">
                                Our medical team is ready to provide personalized care tailored to your needs
                            </p>
                            <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                <button className="px-8 py-3 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition-colors duration-300 shadow-lg hover:shadow-xl">
                                    Book Appointment
                                </button>
                                <button className="px-8 py-3 bg-white text-blue-600 font-semibold rounded-xl border-2 border-blue-600 hover:bg-blue-50 transition-colors duration-300">
                                    Contact Us
                                </button>
                            </div>
                        </div>
                    </div>
                </motion.div>

                {/* Stats */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    viewport={{ once: true }}
                    className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-8 text-center"
                >
                    {[
                        { value: "24/7", label: "Emergency Service" },
                        { value: "20+", label: "Medical Staff" },
                        { value: "6+", label: "Doctors"},
                        { value: "10K+", label: "Patients Served"}
                     ].map((stat, index) => (
                        <div key={index} className="p-6">
                            <div className="text-3xl md:text-4xl font-bold text-blue-600 mb-2">{stat.value}</div>
                            <div className="text-gray-600">{stat.label}</div>
                        </div>
                    ))}
                </motion.div>
            </div>
        </section>
    );
};

export default ServicesOverview;